import {
    PublicClientApplication,
    InteractionRequiredAuthError,
    PopupRequest,
    SilentRequest,
    AccountInfo,
    Configuration, 
    LogLevel
  } from '@azure/msal-browser';

  const clientId = process.env.REACT_APP_CLIENTID !== undefined ? process.env.REACT_APP_CLIENTID : "";
  const authority = process.env.REACT_APP_AUTHORITY !== undefined ? process.env.REACT_APP_AUTHORITY : "";
  const redirectUri = process.env.REACT_APP_REDIRECTURI !== undefined ? process.env.REACT_APP_REDIRECTURI : "";
  
  const msalConfig: Configuration = {
    auth: {
      clientId: clientId,
      authority: authority,
      redirectUri: redirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
          }
        },
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  };
  
  const msalInstance = new PublicClientApplication(msalConfig);
  const msalInitPromise = msalInstance.initialize();

  async function acquireIdToken(): Promise<string | null> {
    await msalInitPromise;
    try {
      const account = getAccount();
  
      if (account) {
        const silentRequest: SilentRequest = {
          scopes: ['openid'],
          account,
          forceRefresh: true
        };
  
        const silentResult = await msalInstance.acquireTokenSilent(silentRequest);
        return silentResult.idToken;
      }
    } catch (error) {
      console.error('Error acquiring token silently:', error);
  
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const interactiveRequest: PopupRequest = {
            scopes: ['openid', 'profile', 'email'],
          };
  
          const interactiveResult = await msalInstance.acquireTokenPopup(interactiveRequest);
          return interactiveResult.idToken;
        } catch (interactiveError) {
          console.error('Error acquiring token interactively:', interactiveError);
        }
      }
    }
  
    return null;
  }
  
  function getAccount(): AccountInfo | null {
    const accounts = msalInstance.getAllAccounts();
  
    if (accounts.length > 0) {
      return accounts[0];
    }
  
    return null;
  }
  
  async function handleRedirectPromise() {
    await msalInitPromise;
    const response = await msalInstance.handleRedirectPromise();
  
    if (response) {
      const account = response.account;
  
      if (account) {
        // Save account data or perform other necessary actions
        console.log('Account data:', account);
      } else {
        console.error('Account data not found in the response');
      }
    }
  }
  
  async function AzureSignIn(): Promise<{ idToken: string | null; error: unknown | null }> {
    await msalInitPromise;
    try {
      const loginRequest: PopupRequest = {
        scopes: ['openid', 'profile', 'email'],
      };
  
      const loginResponse = await msalInstance.loginPopup(loginRequest);
      const account = loginResponse.account;
      const idToken = loginResponse.idToken;
  
      if (account && idToken) {
        return { idToken, error: null };
      } else {
        return { idToken: null, error: 'Authentication failed.' };
      }
    } catch (error) {
      console.error('Error during login:', error);
      return { idToken: null, error: error };
    }
  }
  
  export { msalInstance, acquireIdToken, handleRedirectPromise, getAccount, AzureSignIn };
  
  